import React, { Component, ReactNode } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: any) => void;
  showErrorDetails?: boolean;
  context?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: any;
  retryCount: number;
}

export class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    const context = this.props.context || 'AdminApp';
    console.group(`🔴 [AdminErrorBoundary] Error in ${context}`);
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Retry Count:', this.state.retryCount);
    console.groupEnd();

    this.setState({ errorInfo });
    if (this.props.onError) this.props.onError(error, errorInfo);
  }

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: prevState.retryCount + 1,
    }));
  };

  handleReload = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, retryCount: 0 });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          retryCount={this.state.retryCount}
          context={this.props.context}
          showErrorDetails={this.props.showErrorDetails ?? __DEV__}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }
    return this.props.children;
  }
}

interface FallbackProps {
  error?: Error;
  errorInfo?: any;
  retryCount: number;
  context?: string;
  showErrorDetails?: boolean;
  onRetry: () => void;
  onReload: () => void;
}

const ErrorBoundaryFallback: React.FC<FallbackProps> = ({
  error,
  errorInfo,
  retryCount,
  context,
  showErrorDetails,
  onRetry,
  onReload,
}) => {
  const styles = useStyles();
  const { admin, semantic } = useAdminColors();

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <View style={styles.iconContainer}>
          <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={semantic.status.error} />
        </View>

        <Text style={styles.title}>{context ? `${context} Error` : 'Something went wrong'}</Text>

        <Text style={styles.message}>
          We encountered an unexpected error. This has been logged and our team will investigate.
        </Text>

        {retryCount > 0 && <Text style={styles.retryInfo}>Retry attempts: {retryCount}</Text>}

        <View style={styles.buttonContainer}>
          <TouchableOpacity style={[styles.button, styles.primaryButton]} onPress={onRetry}>
            <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Try Again</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.button, styles.secondaryButton]} onPress={onReload}>
            <SVG_COMPONENTS.CHEVRON_RIGHT
              width={16}
              height={16}
              color={admin.formInput.borderFocused}
            />
            <Text style={styles.secondaryButtonText}>Reload</Text>
          </TouchableOpacity>
        </View>

        {showErrorDetails && error && (
          <View style={styles.errorDetailsContainer}>
            <Text style={styles.errorDetailsTitle}>Error Details (Dev Only)</Text>

            <View style={styles.errorDetail}>
              <Text style={styles.errorDetailLabel}>Error Message:</Text>
              <Text style={styles.errorDetailText}>{error.message}</Text>
            </View>

            {error.stack && (
              <View style={styles.errorDetail}>
                <Text style={styles.errorDetailLabel}>Stack Trace:</Text>
                <ScrollView style={styles.stackTraceContainer} horizontal>
                  <Text style={styles.stackTraceText}>{error.stack}</Text>
                </ScrollView>
              </View>
            )}

            {errorInfo?.componentStack && (
              <View style={styles.errorDetail}>
                <Text style={styles.errorDetailLabel}>Component Stack:</Text>
                <ScrollView style={styles.stackTraceContainer}>
                  <Text style={styles.stackTraceText}>{errorInfo.componentStack}</Text>
                </ScrollView>
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        flex: 1,
        backgroundColor: admin.dataTable.bg,
      },
      scrollContent: {
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
      },
      iconContainer: {
        marginBottom: 24,
        padding: 16,
        borderRadius: 50,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: semantic.status.error,
      },
      title: {
        fontSize: 20,
        fontWeight: '600',
        color: admin.dataTable.text,
        marginBottom: 12,
        textAlign: 'center',
      },
      message: {
        fontSize: 16,
        color: colors.text.dim,
        textAlign: 'center',
        marginBottom: 8,
        lineHeight: 22,
        maxWidth: 300,
      },
      retryInfo: {
        fontSize: 14,
        color: colors.text.muted,
        textAlign: 'center',
        marginBottom: 24,
        fontStyle: 'italic',
      },
      buttonContainer: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 32,
      },
      button: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 8,
        gap: 8,
        minWidth: 120,
        justifyContent: 'center',
      },
      primaryButton: {
        backgroundColor: admin.formInput.borderFocused,
      },
      secondaryButton: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: admin.formInput.borderFocused,
      },
      primaryButtonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '600',
      },
      secondaryButtonText: {
        color: admin.formInput.borderFocused,
        fontSize: 16,
        fontWeight: '600',
      },
      errorDetailsContainer: {
        width: '100%',
        backgroundColor: admin.dataTable.headerBg,
        borderRadius: 8,
        padding: 16,
        marginTop: 16,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
      },
      errorDetailsTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: admin.dataTable.text,
        marginBottom: 12,
      },
      errorDetail: {
        marginBottom: 12,
      },
      errorDetailLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.dim,
        marginBottom: 4,
      },
      errorDetailText: {
        fontSize: 12,
        color: colors.text.muted,
        fontFamily: 'monospace',
      },
      stackTraceContainer: {
        maxHeight: 150,
        backgroundColor: admin.dataTable.bg,
        borderRadius: 4,
        padding: 8,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
      },
      stackTraceText: {
        fontSize: 10,
        color: colors.text.muted,
        fontFamily: 'monospace',
        lineHeight: 14,
      },
    });
  });
